// src/components/Pages/CarBookingPanel.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import './../../style/Vars.css';
import './../../style/MobileStyles.css';

// 🔹 Endpoints y modelos de Seguridad
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { Models } from "../Seguridad/Utils/ModelsSeguridad";

// 🔹 Usuario en sesión
import { getSessionUserId } from "../../utils/SessionUser";

// Íconos para cada submódulo
const submoduleIcons = {
  reservas: '🚗',
  choferes: '👨‍✈️',
  rh: '📋',
};

// Función auxiliar para asignar colores
const getModuleColor = (id) => {
  const colors = {
    reservas: 'blue',
    choferes: 'teal',
    rh: 'purple',
  };
  return colors[id] || 'cyan';
};

// ===================================================================
// Definición base de los submódulos de Car Booking
//  - El campo `id` debe coincidir con lo que guardas en Cat_Recursos.ruta
// ===================================================================
const CAR_BOOKING_SUBMODULES = [
  {
    id: "reservas",
    title: "Panel de Reservas de Autos",
    description: "Creación, revisión y gestión de reservaciones de vehículos.",
    path: "/menu/car-booking/reservas",
  },
  {
    id: "choferes",
    title: "Panel de Choferes",
    description: "Agenda, turnos y viajes asignados para choferes.",
    path: "/menu/car-booking/chofer",
  },
  {
    id: "rh",
    title: "Panel de Recursos Humanos",
    description: "Aprueba o Rechaza Reservas.",
    path: "/menu/car-booking/rh",
  },
];

// Endpoint y modelo para recursos (SEGURIDAD.Cat_Recursos)
const endpointRecursos = ENDPOINTS.recursos; // debe apuntar a "/api/Recursos"

// Intenta usar el modelo de recursos; si cambia el nombre, ajústalo aquí.
const modeloRecurso = Models.catRecursos;

function CarBookingPanel() {
  const navigate = useNavigate();

  // 🔹 Usuario en sesión
  const [sessionUserId] = useState(() => getSessionUserId());

  // 🔹 Submódulos visibles según permisos
  const [visibleSubmodules, setVisibleSubmodules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClick = (path) => {
    navigate(path);
  };

  // ================================================================
  // Cargar permisos desde api/Recursos/{idUsuario}/Panel
  // y filtrar CAR_BOOKING_SUBMODULES por ruta
  // ================================================================
  useEffect(() => {
    async function loadRecursos() {
      try {
        if (!sessionUserId) {
          setErrorMsg(
            "No se pudo identificar al usuario en sesión. No hay paneles disponibles."
          );
          setVisibleSubmodules([]);
          setLoading(false);
          return;
        }

        // Llamada: GET api/Recursos/{idUsuario}/Panel
        const url = `${endpointRecursos}/${sessionUserId}/Panel`;
        const res = await axios.get(url);

        const recursos = (res.data || []).map(modeloRecurso);

        // Tomamos solo recursos:
        //  - activos
        //  - tipo === "Panel"
        //  - con una ruta no vacía
        const allowedIds = new Set(
          recursos
            .filter(
              (r) =>
                (r.tipo || "").toLowerCase() === "panel" &&
                (r.ruta || "").trim() !== ""
            )
            .map((r) => (r.ruta || "").trim())
        );
        console.log(allowedIds)

        // Filtrar los módulos hardcodeados por esos IDs
        const filtered = CAR_BOOKING_SUBMODULES.filter((m) =>
          allowedIds.has(m.id)
        );

        setVisibleSubmodules(filtered);
        setLoading(false);

        // Si no tiene nada asignado, ponemos un mensaje suave
        if (!filtered.length) {
          setErrorMsg(
            "No tienes paneles de Car Booking asignados en este momento."
          );
        }
      } catch (err) {
        console.error("[CarBookingPanel] Error al cargar recursos:", err);
        setErrorMsg(
          "Ocurrió un problema al cargar tus permisos. Intenta de nuevo más tarde."
        );
        setVisibleSubmodules([]); // por seguridad, no mostramos nada
        setLoading(false);
      }
    }

    loadRecursos();
  }, [sessionUserId]);

  return (
    <div className="modules-container">
      <h1>Car Booking</h1>

      {loading ? (
        <div className="modules-loading">Cargando paneles disponibles...</div>
      ) : errorMsg ? (
        <div className="modules-error">{errorMsg}</div>
      ) : (
        <div className="modules-grid">
          {visibleSubmodules.map((submodule) => (
            <div
              key={submodule.id}
              className={`module-card module-card--${getModuleColor(submodule.id)}`}
              onClick={() => handleClick(submodule.path)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && handleClick(submodule.path)}
            >
              <div className="module-card__title">{submodule.title}</div>
              <div className="module-card__description">{submodule.description}</div>
              <div className="module-card__icon" aria-hidden="true">
                {submoduleIcons[submodule.id] || '📁'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Si no hay módulos visibles y no está cargando, mostramos mensaje */}
      {!loading && !visibleSubmodules.length && !errorMsg && (
        <div className="modules-empty">No hay paneles de Car Booking disponibles.</div>
      )}
    </div>
  );
}

export default CarBookingPanel;
